import { useEffect, useMemo, useRef, useState } from "react";
import { SheetDialog } from "./SheetDialog";
import { WheelColumn, type WheelHandle } from "./WheelColumn";
import css from "./TimePicker.module.css";
import { birthYears } from "./birthYears";

/**
 * BirthPicker (A45): дата рождения тремя барабанами день·месяц·год в стилях
 * TimePicker — быстрее системного календаря (не мотать десятилетия по месяцу).
 * value/onCommit — ISO "yyyy-mm-dd"; «Без даты» коммитит пустую строку.
 * Барабаны — WheelColumn (silent-семантика: программные докрутки не пишут state).
 * 03.10 (владелец: дата найма в HR тем же барабаном): fromYear — первый год, clearable=false
 * прячет «Без даты», ageNote=false — подпись «N лет» (для не-рождений она не к месту).
 * 13.10 (ПР8 архива 1С): дата раньше fromYear не поднимается до него — барабан расширяется вниз (birthYears.ts).
 */
const MONTHS = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];
const pad2 = (n: number) => String(n).padStart(2, "0");

export interface BirthPickerProps {
  open: boolean;
  label?: string;
  /** ISO "yyyy-mm-dd" или пусто. */
  value?: string;
  onCommit: (iso: string) => void;
  onCancel: () => void;
  zIndex?: number | string;
  /** Первый год барабана (по умолчанию — 90 лет назад); последний — текущий. */
  fromYear?: number;
  /** Кнопка «Без даты» (по умолчанию есть). */
  clearable?: boolean;
  /** Подпись «N лет · 18+» под датой (по умолчанию есть). */
  ageNote?: boolean;
}

export function BirthPicker({ open, label = "День рождения", value, onCommit, onCancel, zIndex = "calc(var(--z-modal) + 40)", fromYear, clearable = true, ageNote = true }: BirthPickerProps) {
  const nowY = new Date().getFullYear();
  const { y0, y: yInit } = birthYears(value, fromYear, nowY);   // ПР8 архива 1С: год значения не поднимается до fromYear
  const years = useMemo(() => Array.from({ length: nowY - y0 + 1 }, (_, i) => String(y0 + i)), [nowY, y0]);
  const days = useMemo(() => Array.from({ length: 31 }, (_, i) => pad2(i + 1)), []);
  const init = () => {
    const mm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
    return { y: yInit, mo: mm ? +mm[2]! - 1 : 0, d: mm ? +mm[3]! : 1 };
  };
  const [v, setV] = useState(init);
  const wd = useRef<WheelHandle>(null), wm = useRef<WheelHandle>(null), wy = useRef<WheelHandle>(null);

  /* Реинициализация на каждое открытие (паттерн TimePicker). */
  useEffect(() => {
    if (!open) return;
    const s = init();
    setV(s);
    wd.current?.setValue(pad2(s.d), false);
    wm.current?.setValue(MONTHS[s.mo]!, false);
    wy.current?.setValue(String(s.y), false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /* 31→кламп к реальному числу дней месяца при коммите. */
  const commit = () => {
    const dim = new Date(v.y, v.mo + 1, 0).getDate();
    onCommit(`${v.y}-${pad2(v.mo + 1)}-${pad2(Math.min(v.d, dim))}`);
  };

  /* 26.08 (владелец): под выбранной датой — полных лет (и «18+» при совершеннолетии) */
  const age = (() => { const t = new Date(); let a = t.getFullYear() - v.y; if (t.getMonth() < v.mo || (t.getMonth() === v.mo && t.getDate() < v.d)) a--; return a; })();
  const yearsWord = (n: number) => { const m10 = n % 10, m100 = n % 100; return m10 === 1 && m100 !== 11 ? "год" : m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20) ? "года" : "лет"; };

  return (
    <SheetDialog open={open} onDismiss={onCancel} zIndex={zIndex} maxHeight="55dvh" ariaLabel={label}>
      <div className={css.head}>
        <span className={css.label}>{label}</span>
        <span className={css.preview}><span className={css.segv}>{pad2(v.d)}.{pad2(v.mo + 1)}.{v.y}</span>{ageNote && age >= 0 && age <= 120 ? <span className={css.ageNote}>{age} {yearsWord(age)}{age >= 18 ? " · 18+" : ""}</span> : null}</span>
      </div>
      <div className={css.zone}>
        <div className={css.wheels}>
          <WheelColumn values={days} initial={pad2(v.d)} onUserChange={(x) => setV((p) => ({ ...p, d: +x }))} handleRef={wd} ariaLabel="День" />
          <WheelColumn values={MONTHS} initial={MONTHS[v.mo]!} onUserChange={(x) => setV((p) => ({ ...p, mo: Math.max(0, MONTHS.indexOf(x)) }))} handleRef={wm} ariaLabel="Месяц" />
          <WheelColumn values={years} initial={String(v.y)} onUserChange={(x) => setV((p) => ({ ...p, y: +x }))} handleRef={wy} ariaLabel="Год" />
          <div className={css.selframe}>
            <span className={`${css.corner} ${css.tl}`} /><span className={`${css.corner} ${css.tr}`} />
            <span className={`${css.corner} ${css.bl}`} /><span className={`${css.corner} ${css.br}`} />
          </div>
        </div>
        <div className={css.scan} />
      </div>
      <div className={css.foot}>
        <button type="button" className={`${css.btn} ${css.cancel}`} onClick={onCancel}>Отмена</button>
        {clearable ? <button type="button" className={`${css.btn} ${css.cancel}`} onClick={() => onCommit("")}>Без даты</button> : null}
        <button type="button" className={`${css.btn} ${css.go}`} onClick={commit}>Готово</button>
      </div>
    </SheetDialog>
  );
}
